import { Navigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";

import {
  ContentContainer,
  ContentHeader,
  PageTitle,
} from "features/layout/components";

import useWorkspaceNavigate from "common/hooks/useWorkspaceNavigate";

import useLeadListSourceMutations from "../../datahooks/useLeadListSourceMutations";
import ConnectCrm from "./ConnectCrm";
import CustomList from "./CustomList";
import Event from "./Event";
import Group from "./Group";
import Leads from "./Leads";
import Navigator from "./Navigator";
import Post from "./Post";
import Search from "./Search";

import type { FunctionComponent } from "react";
import type { LeadListSourceType } from "../../types";

export default function AddSourcePage() {
  const { listId, sourceType } = useParams<{
    listId: string;
    sourceType: LeadListSourceType;
  }>();
  const { relativeNavigate } = useWorkspaceNavigate();

  const { createSource, isCreatingSource } = useLeadListSourceMutations();

  function onCreateSource(
    params: Parameters<typeof createSource>["0"]["params"],
  ) {
    createSource({ listId, params })
      .then(() => {
        relativeNavigate(`lists/${listId}`);
      })
      .catch(() => {
        toast.error("Error creating source");
      });
  }

  //every source component takes the same props, each one narrowing the params
  //it passes to createSource down to its own source type
  let Component: FunctionComponent<{
    createSource: typeof onCreateSource;
    isCreatingSource: boolean;
  }>;
  let pageTitle: string;

  if (sourceType === "navigator") {
    Component = Navigator;
    pageTitle = "Sales Navigator";
  } else if (sourceType === "post") {
    Component = Post;
    pageTitle = "Post Engagement";
  } else if (sourceType === "event") {
    Component = Event;
    pageTitle = "Event attendees";
  } else if (sourceType === "group") {
    Component = Group;
    pageTitle = "Group members";
  } else if (sourceType === "search") {
    Component = Search;
    pageTitle = "Create LinkedIn search source";
  } else if (sourceType === "list") {
    Component = CustomList;
    pageTitle = "Import custom list";
  } else if (sourceType === "crm") {
    Component = ConnectCrm;
    pageTitle = "Connect CRM";
  } else if (sourceType === "drip") {
    //drip is what the guard above leaves
    Component = Leads;
    pageTitle = "Search your contacts";
  } else {
    return <Navigate to=".." replace />;
  }

  return (
    <ContentContainer>
      <ContentHeader>
        <PageTitle>{pageTitle}</PageTitle>
      </ContentHeader>
      <Component
        createSource={onCreateSource}
        isCreatingSource={isCreatingSource}
      />
    </ContentContainer>
  );
}
